/**
 * Static UI locale (en / ta). No Google page translate.
 * Persists preference in localStorage + cookie so PWA / private modes /
 * Safari ITP still recover the choice after reload when possible.
 */
const COOKIE_NAME = 'googtrans'
const UI_LANG_COOKIE = 'swotify_ui_lang'
const STORAGE_KEY = 'swotify_translate_lang'
const SWITCH_KEY = 'swotify_lang_switching'

/** In-memory fallback when storage is blocked (private mode). */
let memoryLang = null
let bootstrapped = false

function cookieExpiresPast() {
  return 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
}

function cookieExpiresFar() {
  return 'expires=Thu, 31 Dec 2099 23:59:59 GMT'
}

function hostCookieTargets() {
  const host = (typeof window !== 'undefined' && window.location.hostname) || ''
  const targets = [{ path: '/' }]
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return targets
  }
  targets.push({ path: '/', domain: host })
  targets.push({ path: '/', domain: `.${host}` })
  return targets
}

function writeCookie(name, value, expires) {
  if (typeof document === 'undefined') return
  hostCookieTargets().forEach((target) => {
    const domain = target.domain ? `; domain=${target.domain}` : ''
    document.cookie = `${name}=${encodeURIComponent(value)}; path=${target.path}${domain}; ${expires}; SameSite=Lax`
  })
}

function readCookie(name) {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  if (!match) return ''
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

function clearGoogTransCookie() {
  if (typeof document === 'undefined') return
  writeCookie(COOKIE_NAME, '', cookieExpiresPast())
}

function normalizeLang(value) {
  const code = String(value || '')
    .trim()
    .toLowerCase()
  return code === 'ta' || code.startsWith('ta-') ? 'ta' : 'en'
}

function readStoredLanguage() {
  if (memoryLang === 'ta' || memoryLang === 'en') return memoryLang
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === 'ta') {
      return 'ta'
    }
  } catch {
    /* private mode */
  }
  const fromCookie = normalizeLang(readCookie(UI_LANG_COOKIE))
  if (fromCookie === 'ta') return 'ta'
  return 'en'
}

function writeStoredLanguage(lang) {
  const target = lang === 'ta' ? 'ta' : 'en'
  memoryLang = target
  try {
    if (typeof localStorage !== 'undefined') {
      if (target === 'ta') localStorage.setItem(STORAGE_KEY, 'ta')
      else localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    /* private mode — cookie / memory still hold preference */
  }
  if (target === 'ta') writeCookie(UI_LANG_COOKIE, 'ta', cookieExpiresFar())
  else writeCookie(UI_LANG_COOKIE, '', cookieExpiresPast())
}

function syncDocumentLang(lang) {
  if (typeof document === 'undefined') return
  document.documentElement.lang = lang === 'ta' ? 'ta' : 'en'
  document.documentElement.setAttribute('data-sw-lang', lang === 'ta' ? 'ta' : 'en')
}

function ensureBootstrapped() {
  if (bootstrapped || typeof document === 'undefined') return
  bootstrapped = true
  clearGoogTransCookie()
  const lang = readStoredLanguage()
  memoryLang = lang
  // Re-write so cookie and storage stay aligned after upgrades / PWA restore.
  writeStoredLanguage(lang)
  syncDocumentLang(lang)
}

export function getTranslateLanguage() {
  if (typeof document === 'undefined') return 'en'
  ensureBootstrapped()
  return readStoredLanguage()
}

function languageSwitchLabel(lang) {
  return lang === 'ta' ? 'ஏற்றுகிறது…' : 'Loading…'
}

function showLanguageSwitchLoader(lang) {
  if (typeof document === 'undefined') return
  try {
    sessionStorage.setItem(SWITCH_KEY, '1')
  } catch {
    /* private mode */
  }
  document.documentElement.classList.add('sw-lang-switching')
  const label = document.getElementById('lang-switch-loader-label')
  if (label) label.textContent = languageSwitchLabel(lang)
}

function hideLanguageSwitchLoader() {
  try {
    sessionStorage.removeItem(SWITCH_KEY)
  } catch {
    /* private mode */
  }
  document.getElementById('pwa-splash')?.remove()
  document.documentElement.classList.remove('sw-lang-switching')
}

function currentPath() {
  const hash = window.location.hash || ''
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  const path = (raw.split('?')[0] || '/').replace(/\/+$/, '') || '/'
  return path
}

function isEntryRoute() {
  const path = currentPath()
  return (
    path === '/' ||
    path === '/login' ||
    path === '/signup' ||
    path === '/terms' ||
    path === '/privacy' ||
    path === '/help'
  )
}

function rootHasContent() {
  const root = document.getElementById('root')
  if (!root || root.childElementCount === 0) return false
  const text = (root.innerText || '').replace(/\s+/g, ' ').trim()
  if (text.length <= 12) return false
  const loader = root.querySelector('.sw-loader')
  if (loader && root.childElementCount <= 2 && !root.querySelector('nav')) {
    return false
  }
  return true
}

function appShellReady() {
  if (isEntryRoute()) return false
  const root = document.getElementById('root')
  if (!root?.querySelector('nav')) return false
  return (root.innerText || '').replace(/\s+/g, ' ').trim().length > 12
}

/** Hold a loader from sign-in until the dashboard sidebar is actually painted. */
export function coverUntilAppShell() {
  if (typeof document === 'undefined') return
  ensureBootstrapped()
  document.getElementById('pwa-splash')?.remove()
  document.documentElement.classList.add('sw-lang-switching')
  const label = document.getElementById('lang-switch-loader-label')
  if (label) label.textContent = languageSwitchLabel(getTranslateLanguage())
  const started = Date.now()
  const tick = () => {
    if (appShellReady() || Date.now() - started > 8000) {
      document.documentElement.classList.remove('sw-lang-switching')
      return
    }
    window.requestAnimationFrame(tick)
  }
  window.requestAnimationFrame(tick)
}

export function beginLanguageSwitchCover() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  ensureBootstrapped()
  let switching = document.documentElement.classList.contains('sw-lang-switching')
  if (!switching) {
    try {
      switching = sessionStorage.getItem(SWITCH_KEY) === '1'
    } catch {
      switching = false
    }
  }
  if (!switching) return
  document.documentElement.classList.add('sw-lang-switching')
  const started = Date.now()
  const minMs = 450
  const maxMs = 8000
  const tick = () => {
    const elapsed = Date.now() - started
    if ((elapsed >= minMs && rootHasContent()) || elapsed >= maxMs) {
      hideLanguageSwitchLoader()
      return
    }
    window.requestAnimationFrame(tick)
  }
  window.requestAnimationFrame(tick)
}

export function setTranslateLanguage(lang) {
  const target = lang === 'ta' ? 'ta' : 'en'
  ensureBootstrapped()
  writeStoredLanguage(target)
  clearGoogTransCookie()
  syncDocumentLang(target)
  showLanguageSwitchLoader(target)
  // Full reload so every mounted Chrome/t() call re-reads locale (HashRouter + PWA safe).
  window.location.reload()
}

/** No-op: Google page translate is disabled (static dictionaries only). */
export function markUntranslatableFields() {}

/** No-op: Google page translate is disabled (static dictionaries only). */
export function reapplyPageTranslate() {}

/** No-op: Google page translate is disabled (static dictionaries only). */
export function scheduleReapplyPageTranslate() {}

/** No-op: Google page translate is disabled (static dictionaries only). */
export function loadGoogleTranslate() {}
